import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";

const empty = { nome: "", cpf: "", telefone: "", email: "", admissao: "", ativo: true };

export default function Sellers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/sellers").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.put(`/sellers/${editing.id}`, form);
      else await api.post("/sellers", form);
      toast.success("Salvo"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Excluir vendedor "${s.nome}"?`)) return;
    try { await api.delete(`/sellers/${s.id}`); toast.success("Vendedor excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const canEdit = can(user, "manage_sales") || user?.role === "administrativo" || user?.role === "gestao";
  const canViewComm = can(user, "view_commissions");
  return (
    <Layout title="Vendedores">
      {canEdit && (
        <div className="flex justify-end mb-4">
          <Button data-testid="seller-new" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}><Plus className="w-4 h-4 mr-1"/>Novo</Button>
        </div>
      )}
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow>
            <TableHead>Nome</TableHead><TableHead>CPF</TableHead><TableHead>Telefone</TableHead>
            <TableHead>Vendas mês</TableHead><TableHead>Vendas ano</TableHead>
            {canViewComm && <><TableHead>Com. Pend.</TableHead><TableHead>Bon. Pend.</TableHead></>}
            <TableHead>Status</TableHead><TableHead className="text-right"></TableHead>
          </TableRow></TableHeader>
          <TableBody>{items.map(s => (
            <TableRow key={s.id}>
              <TableCell className="font-medium">{s.nome}</TableCell>
              <TableCell>{s.cpf}</TableCell><TableCell>{s.telefone}</TableCell>
              <TableCell>{fmtBRL(s.total_mes)}</TableCell><TableCell>{fmtBRL(s.total_ano)}</TableCell>
              {canViewComm && <><TableCell>{fmtBRL(s.comissao_pendente)}</TableCell><TableCell>{fmtBRL(s.bonificacao_pendente)}</TableCell></>}
              <TableCell><Badge variant={s.ativo ? "default" : "secondary"}>{s.ativo ? "Ativo" : "Inativo"}</Badge></TableCell>
              <TableCell className="text-right">
                {canEdit && <>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(s); setForm(s); setOpen(true); }} data-testid={`seller-edit-${s.id}`}><Pencil className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`seller-delete-${s.id}`}><Trash2 className="w-4 h-4"/></Button>
                </>}
              </TableCell>
            </TableRow>
          ))}{items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Sem vendedores</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} Vendedor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["nome", "Nome", 2], ["cpf", "CPF"], ["telefone", "Telefone"], ["email", "E-mail", 2], ["admissao", "Admissão", 1, "date"]].map(([k, l, s = 1, t = "text"]) => (
              <div key={k} className={`col-span-${s}`}>
                <Label className="text-xs font-semibold">{l}</Label>
                <Input data-testid={`seller-${k}`} type={t} value={form[k] || ""} onChange={(e) => setForm({...form, [k]: e.target.value})} className="mt-1"/>
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={save} data-testid="seller-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
